import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
// import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { fetch_one, fetch_all } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import {
  AppBar,
  Button as Btn,
  DialogActions,
  Divider,
  Grid,
  MenuItem,
  Tab,
  Tabs,
} from '@mui/material';

import {
  Editor,
  Select,
  TextInput,
  Typography
} from '../../../../components/v2/styled';
import { Base } from '../Base';

import { KnowledgeCourse } from '../../../../models/KnowledgeCourse';
import { KnowledgeLesson } from '../../../../models/KnowledgeLesson';
import { KnowledgeSubject } from '../../../../models/KnowledgeSubject';
import { Questionnaire } from '../../../../models/Questionnaire';

import HelpersLanguage from '../../../../actions/helpers/language';

// const { DateTime } = require("luxon");

type Props = {
  knowledgeCourse: KnowledgeCourse;
  knowledgeSubject: KnowledgeSubject;
  objectId?: number;
  setObjectId?: (n?:number) => void;
  refetchSubjects: () => void;
  open: boolean;
  setOpen: (b:boolean) => void;
};

export const KnowledgeAddLesson: FunctionComponent<Props> = ({refetchSubjects, knowledgeCourse, knowledgeSubject, objectId, setObjectId, open, setOpen}) => {
  const theme = useTheme();
  // const navigate = useNavigate();
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const {t, i18n} = useTranslation(['translations']);
  const queryClient = useQueryClient();

  const [mainObject, setMainObject] = useState<KnowledgeLesson>({
    name: '',
    weight: (((knowledgeSubject.weight || 0) * 10) + (knowledgeSubject.knowledge_lessons || []).length * 10) + 10
  });
  const [currentLanguageId, setCurrentLanguageId] = useState<number>(1);
  const [flickerEditor, setFlickerEditor] = useState<boolean>(false);

  const {
    data: object,
    isLoading: objectLoading,
    isError: objectError,
    isSuccess: objectSuccess,
    refetch: objectRefetch,
  } = useQuery({
    queryKey: ["knowledge_lesson", "for_add", objectId],
    queryFn: () =>
      fetch_one<KnowledgeLesson>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'knowledge_lesson',
          id: objectId,
          fields: [
            'id', 'full_name', 'full_body', 'knowledge_course_id', 'knowledge_subject_id', 'quiz_id', 'weight', 'extra_data', 'full_public_duration', 'full_real_duration', 'get_video',
            'access_data'
          ],
          practice_id: (login?.practice_id || currentPractice?.id)
        },
        login
      ),
    // select: (d) => {
    //   return {
    //     ...d,
    //     birth_date: DateTime.fromISO(d.birth_date).toFormat("dd/LL/y")
    //   };
    // },
    enabled: !!objectId,
  });

  const {
    data: questionnaires,
    // isLoading: objectsLoading,
    // isError: objectsError,
    isSuccess: questionnairesSuccess,
    refetch: questionnairesRefetch,
  } = useQuery({
    queryKey: ["coach", "questionnaires", (login?.practice_id || currentPractice?.id)],
    queryFn: () =>
      fetch_all<Questionnaire>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'questionnaire',
          fields: ['id', 'name', 'content_type'],
          order: 'name ASC',
          per_page: 200,
          page: 0,
          filter: {
            search: '',
            advanced: {
              only_for_knowledge: 1,
              practice_id: (login?.practice_id || currentPractice?.id)
            }
          },
          practice_id: (login?.practice_id || currentPractice?.id)
        },
        login
      ),
    enabled: !!(login?.practice_id || currentPractice?.id),
  });










  const mutationSave = useMutation({
    mutationFn: (pars: {
      formData: any;
      keepOpen?: boolean;
    }) => {
      return fetch_one<KnowledgeLesson>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/save`,
        pars.formData,
        login
      );
    },
    onMutate: (pars: {
      formData: any;
    }) => {
      return true;
    },
    onError: (data, variables, context) => {
      return true;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["knowledge_lesson", "for_add", objectId] });
      refetchSubjects();
      if (!!setObjectId) objectRefetch();
      
      if (!variables.keepOpen) {
        setFlickerEditor(true);
        // setMainObject({name: ''});
        setTimeout(() => {
          setFlickerEditor(false);
          if (!!setObjectId) setObjectId(undefined);
          // setMainObject({name: ''});
          setOpen(false);
        }, 200);
      }
    },
  });




  










  const saveObject = (keepOpen: boolean) => {
    let data = new FormData();
    data.append('object', 'knowledge_lesson');
    data.append('handler_id', login?.id?.toString() || '');
    data.append('practice_id', (login?.practice_id || currentPractice?.id || '').toString() || '');
    if (!!mainObject?.id) data.append('id', mainObject?.id?.toString() || '');
    if (!!mainObject?.id) data.append('ob[id]', mainObject?.id?.toString() || '');
    data.append('ob[knowledge_course_id]', knowledgeCourse?.id?.toString() || '');
    data.append('ob[knowledge_subject_id]', knowledgeSubject?.id?.toString() || '');
    data.append('ob[body]', (mainObject?.full_body || {})[1]?.toString() || '');
    data.append('ob[name]', (mainObject?.full_name || {})[1]?.toString() || '');
    data.append('ob[public_duration]', (mainObject?.full_public_duration || {})[1]?.toString() || '');
    data.append('ob[real_duration]', (mainObject?.full_real_duration || {})[1]?.toString() || '');
    data.append('ob[weight]', parseInt(mainObject?.weight?.toString() || '100', 10).toString());
    data.append('ob[access_data][day_from_start]', parseInt(mainObject?.access_data?.day_from_start?.toString() || '0', 10).toString());
    data.append('ob[quiz_id]', mainObject?.quiz_id?.toString() || '');
    if (!!mainObject?.video) {
      data.append(`ob[video]`, mainObject?.video);
    }

    Object.keys(mainObject?.full_body || {}).map((key) => {
      if (parseInt(key, 10) !== 1) {
        data.append(`translations[body][${key}]`, (mainObject?.full_body || {})[parseInt(key, 10)]?.toString() || '');
      }
    });
    Object.keys(mainObject?.full_name || {}).map((key) => {
      if (parseInt(key, 10) !== 1) {
        data.append(`translations[name][${key}]`, (mainObject?.full_name || {})[parseInt(key, 10)]?.toString() || '');
      }
    });
    Object.keys(mainObject?.full_public_duration || {}).map((key) => {
      if (parseInt(key, 10) !== 1) {
        data.append(`translations[public_duration][${key}]`, (mainObject?.full_public_duration || {})[parseInt(key, 10)]?.toString() || '');
      }
    });
    Object.keys(mainObject?.full_real_duration || {}).map((key) => {
      if (parseInt(key, 10) !== 1) {
        data.append(`translations[real_duration][${key}]`, (mainObject?.full_real_duration || {})[parseInt(key, 10)]?.toString() || '');
      }
    });
    
    mutationSave.mutate({
      formData: data,
      keepOpen: keepOpen
    });
  };
  
  useEffect(() => {
    // console.log("log", mainObject, object, objectId, open, objectSuccess);
    if (!!objectSuccess) {
      // console.log('setting mo', object);
      setTimeout(() => {setMainObject({...object});}, 250);
    }
  }, [objectSuccess, object]);
  
  // useEffect(() => {
  //   console.log("mo", mainObject);
  // }, [mainObject]);
  
  useEffect(() => {
    // console.log('object_id', open, objectId);
    if (!objectId) setMainObject({
      name: '',
      weight: (((knowledgeSubject.weight || 0) * 10) + (knowledgeSubject.knowledge_lessons || []).length * 10) + 10
    });
    if (!!objectId) objectRefetch();
  }, [objectId]);
  
  useEffect(() => {
    // console.log('open', open, objectId);
    if (!objectId) setMainObject({
      name: '',
      weight: (((knowledgeSubject.weight || 0) * 10) + (knowledgeSubject.knowledge_lessons || []).length * 10) + 10
    });
  }, [knowledgeSubject]);

  return (<>
    <Base
      name="add_lesson"
      title={t("coach.knowledge_lessons.dialogs.add_edit")}
      maxWidth='xl'
      onClose={() => {
        queryClient.invalidateQueries({ queryKey: ["knowledge_lesson", "for_add", objectId] });
        if (!!setObjectId) setObjectId(undefined);
        setMainObject({name: ''});
      }}
      content={<>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{marginTop: 4}}>
            <Typography variant="h5">{t("coach.submenu.lesson_sections.general")}</Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
            <AppBar position="static" sx={{backgroundColor: theme.palette.primary.contrastText}}>
              <Tabs value={currentLanguageId} onChange={(ev, i) => {
                setCurrentLanguageId(i);
                setFlickerEditor(true);
                setTimeout(() => {setFlickerEditor(false);}, 200);
              }}>
                {(knowledgeCourse.access_data?.availability || [1]).map(mmm => HelpersLanguage.mapping_to_iso(mmm)).map((locale, i) => {
                  return (<Tab value={HelpersLanguage.mapping_to_id(locale)} label={locale.toUpperCase()} id={`locale_tab_${locale}`} />)
                })}
              </Tabs>
            </AppBar>
          </Grid>
          <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
            <TextInput
              id="name"
              caption={t("general.knowledge_lessons.fields.name")}
              starred
              value={(mainObject?.full_name || {})[currentLanguageId] || ""}
              onChange={(e) => {
                setMainObject({
                  ...(mainObject || {name: ''}),
                  full_name: {
                    ...(mainObject?.full_name || {}),
                    [currentLanguageId]: e
                  }
                });
              }}
            />
          </Grid>
          <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
            <Typography sx={{fontSize: '0.8rem'}}>{t("general.knowledge_lessons.fields.body")}</Typography>
            {!flickerEditor && <Editor
              knowledge
              label=""
              value={(mainObject?.full_body || {})[currentLanguageId] || ""}
              onChange={(e) => {
                setMainObject({
                  ...(mainObject || {name: ''}),
                  full_body: {
                    ...(mainObject?.full_body || {}),
                    [currentLanguageId]: e
                  }
                });
              }}
            />}
          </Grid>
          {/* <Grid item xs={12} sx={{marginTop: 4}}>
            <Typography variant="h5">{t("coach.submenu.lesson_sections.video")}</Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography sx={{fontSize: '0.8rem'}}>{t("general.knowledge_lessons.fields.video")}</Typography>
            <ImagePicker
              existing={mainObject?.get_video}
              deleteable
              video
              backend
              item={item}
              itemUrl={itemUrl}
              onSelect={(_item:File|undefined, _itemUrl:string|undefined) => {
                setItem(_item);
                setItemUrl(_itemUrl);
                setMainObject({
                  ...mainObject,
                  video: _item,
                  videoUrl: _itemUrl
                });
                if (!_item && !_itemUrl) {
                  setItemCleared(true);
                } else {
                  setItemCleared(false);
                }
              }}
            />
          </Grid> */}
          <Grid item xs={12} sx={{marginTop: 4}}>
            <Typography variant="h5">{t("coach.submenu.lesson_sections.duration")}</Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
            <AppBar position="static" sx={{backgroundColor: theme.palette.primary.contrastText}}>
              <Tabs value={currentLanguageId} onChange={(ev, i) => {
                setCurrentLanguageId(i);
                setFlickerEditor(true);
                setTimeout(() => {setFlickerEditor(false);}, 200);
              }}>
                {(knowledgeCourse.access_data?.availability || [1]).map(mmm => HelpersLanguage.mapping_to_iso(mmm)).map((locale, i) => {
                  return (<Tab value={HelpersLanguage.mapping_to_id(locale)} label={locale.toUpperCase()} id={`locale_tab_${locale}`} />)
                })}
              </Tabs>
            </AppBar>
          </Grid>
          <Grid item xs={12} md={6} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
            <TextInput
              id="public_duration"
              caption={t("general.knowledge_lessons.fields.public_duration")}
              starred
              value={(mainObject?.full_public_duration || {})[currentLanguageId] || ""}
              onChange={(e) => {
                setMainObject({
                  ...(mainObject || {name: ''}),
                  full_public_duration: {
                    ...(mainObject?.full_public_duration || {}),
                    [currentLanguageId]: e
                  }
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{borderLeft: "4px solid rgba(0, 0, 0, 0.54)"}}>
            <TextInput
              id="real_duration"
              caption={t("general.knowledge_lessons.fields.real_duration")}
              placeholder="HH:MM"
              starred
              value={(mainObject?.full_real_duration || {})[currentLanguageId] || ""}
              onChange={(e) => {
                setMainObject({
                  ...(mainObject || {name: ''}),
                  full_real_duration: {
                    ...(mainObject?.full_real_duration || {}),
                    [currentLanguageId]: e
                  }
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              id="access_day_from_start"
              caption={t("general.knowledge_lessons.fields.access_day_from_start")}
              starred
              value={mainObject?.access_data?.day_from_start || ""}
              onChange={(e) => {
                setMainObject({
                  ...(mainObject || {name: ''}),
                  access_data: {
                    ...(mainObject?.access_data || {}),
                    day_from_start: !!e ? parseInt(e, 10)?.toString() : '0'
                  }
                });
              }}
            />
          </Grid>

          <Grid item xs={12} sx={{marginTop: 4}}>
            <Typography variant="h5">{t("coach.submenu.lesson_sections.order")}</Typography>
            <Divider />
          </Grid>
          {!!knowledgeSubject.is_open_order && <Grid item xs={12} sx={{marginTop: 1}}>
            <Typography html sx={{
              borderRadius: 1,
              border: '1px solid transparent',
              borderColor: theme.palette.warning.main,
              padding: 1,
              textAlign: 'center'
            }}>{t("knowledge_subjects.warnings.subject_has_open_order")}</Typography>
          </Grid>}
          <Grid item xs={12} md={6}>
            <TextInput
              id="weight"
              caption={t("coach.models.knowledge_lessons.weight")}
              value={mainObject?.weight?.toString() || ''}
              onChange={(e) => {
                setMainObject({
                  ...(mainObject || {name: ''}),
                  weight: !!e ? (isNaN(parseInt(e || '100', 10)) ? undefined : parseInt(e || '100', 10)) : undefined
                });
              }}
            />
          </Grid>
          
          <Grid item xs={12} sx={{marginTop: 4}}>
            <Typography variant="h5">{t("coach.submenu.lesson_sections.quiz")}</Typography>
            <Divider />
          </Grid>
          <Grid item xs={12} md={6}>
            <Select
              fullWidth
              caption={t("general.knowledge_lessons.fields.quiz_id")}
              backend
              displayEmpty
              id="quiz_id"
              value={mainObject?.quiz_id?.toString() || ''}
              setValue={(v) => {
                setMainObject({
                  ...(mainObject || {name: ''}),
                  quiz_id: !!v ? parseInt(v.toString(), 10) : undefined
                });
              }}
            >
              <MenuItem value=''>--- ---</MenuItem>
              {(questionnaires || []).filter(fff => fff.content_type === 'quiz').map(questionnaire => <MenuItem value={questionnaire.id}>{questionnaire.name}</MenuItem>)}
            </Select>
          </Grid>

          {/* <Grid item xs={12} sx={{marginTop: 4}}>
            <Typography variant="h5">{t("coach.submenu.lesson_sections.attachments")}</Typography>
            <Divider />
          </Grid> */}

          {/* voeg attachments toe, en laat zien in builder */}

        </Grid>
      </>}
      actions={<DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
        <Btn
          disabled={mutationSave.isPending}
          onClick={(e) => {
            queryClient.invalidateQueries({ queryKey: ["knowledge_lesson", "for_add", objectId] });
            if (!!setObjectId) setObjectId(undefined);
            setMainObject({name: ''});
            setOpen(false);
          }}
          color="primary"
        >{t("coach.general.actions.cancel")}</Btn>
        <Btn
          disabled={mutationSave.isPending || Object.values(mainObject?.full_name || {}).filter(fff => !!fff).length < 1}
          onClick={(e) => {
            saveObject(false);
          }}
          color="primary"
        >{t("client.general.actions.save")}</Btn>
        <Btn
          disabled={mutationSave.isPending || Object.values(mainObject?.full_name || {}).filter(fff => !!fff).length < 1}
          onClick={(e) => {
            saveObject(true);
          }}
          color="primary"
        >{t("coach.general.actions.save_and_keep_open")}</Btn>
      </DialogActions>}
      open={open}
      setOpen={setOpen}
    />
  </>);
}
